import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "drv-form-root" }
const _hoisted_2 = {
  key: 0,
  class: "drv-text-body-md drv-text-weight-medium"
}
const _hoisted_3 = {
  key: 0,
  class: "drv-py-2"
}

import { computed, ref } from "vue";
import {
  ETelematicsMessagesKeys,
  IPlatform,
  IBaseAccountNameWOCredsConfirm,
  EAuthTypes,
  ECommonMessages,
  EPlatformName,
} from "../../../../types";
import { Input } from "draivn-ui-kit/components/input";
import {
  maxLengthValidator,
  minLengthValidator,
  requiredValidator,
} from "draivn-ui-domain";
import HelpLink from "../HelpLink.vue";
import { isHosConnectPlatform } from "../../../../services/hos-connect";


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseAccountNameWOInstructionsConfirmation',
  props: {
    modelValue: {},
    platform: {},
    disable: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit =
  __emit;

const EMPTY_MODEL = { type: EAuthTypes.ACCOUNT, account_name: "" };

const icon = {
  name: "circle-information",
  size: 16,
};

const model = computed<IBaseAccountNameWOCredsConfirm>({
  get() {
    return props.modelValue ?? EMPTY_MODEL;
  },
  set(value: IBaseAccountNameWOCredsConfirm) {
    emit("update:modelValue", value);
  },
});

const accountNumberModel = computed<string>({
  get() {
    return props.modelValue?.account_name ?? EMPTY_MODEL.account_name;
  },
  set(value: string) {
    model.value = { ...model.value, account_name: value };
  },
});

const accountNumberControl = ref<typeof Input | null>(null);
const hasError = computed<boolean>(() => {
  return !accountNumberModel.value || accountNumberControl.value?.hasError;
});

const DRIVE_HOS_FAMILY = new Set<EPlatformName>([
  EPlatformName.DRIVE_HOS,
  EPlatformName.FACTOR_ELD,
  EPlatformName.DAILY_ELD_PLUS,
  EPlatformName.DSG_LOGS,
  EPlatformName.DY_PLUS_ELD,
  EPlatformName.EGREEN_ELD_PRO,
  EPlatformName.UZBPRIME_ELD,
  EPlatformName.ILOG_ELD_APP,
  EPlatformName.LEADER_ELD,
  EPlatformName.LUCID_ELD,
  EPlatformName.PANDA_ELD,
  EPlatformName.PRESTIGE_ELD,
  EPlatformName.STARTRUCKS_ELD,
  EPlatformName.US_FAST_ELD,
  EPlatformName.US_POWER_ELD,
  EPlatformName.VITALITY_ELD,
  EPlatformName.ZEROMAX_ELD,
  EPlatformName.BLUE_STAR_ELD,
  EPlatformName.SECURE_PATH_ELD,
  EPlatformName.FIRST_ELD,
  EPlatformName.DRIVE_LENS_ELD,
  EPlatformName.NIGHT_WATCH_ELD,
]);

const TT_ELD_FAMILY = new Set<EPlatformName>([
  EPlatformName.TT_ELD,
  EPlatformName.EVO_ELD,
  EPlatformName.ONTIME_LOGS,
  EPlatformName.ZIPPY_ELD,
  EPlatformName.ONTIME_ELD,
  EPlatformName.FIVE_ELD,
  EPlatformName.LOGFLEET_ELD,
  EPlatformName.SUN_ELD,
]);

const textByPlatformName: Partial<
  Record<
    EPlatformName,
    {
      title: string;
      url: string;
      fieldName: string;
      titleParams?: Record<string, string>;
      fieldNameParams?: Record<string, string>;
      snackbarText?: string;
      isNeedSnackbar: boolean;
    }
  >
> = {
  [EPlatformName.GPS_INSIGHT]: {
    title: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.GPS_INSIGHT_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: true,
  },
  [EPlatformName.OMNITRACS]: {
    title: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.OMNITRACS_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: true,
  },
  [EPlatformName.XRS]: {
    title: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.XRS_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: true,
  },
  [EPlatformName.LYTX]: {
    title: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.LYTX_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: true,
  },
  [EPlatformName.FLEET_UP]: {
    title: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.FLEET_UP_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: true,
  },
  [EPlatformName.ISAAC]: {
    title: ETelematicsMessagesKeys.ISAAC_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.ISAAC_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.ISAAC_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: false,
  },
  [EPlatformName.WORKWAVE]: {
    title: ETelematicsMessagesKeys.WORKWAVE_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.WORKWAVE_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.WORKWAVE_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: false,
  },
  [EPlatformName.TELETRAC_NAVMAN]: {
    title: ETelematicsMessagesKeys.TELETRAC_NAVMAN_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.TELETRAC_NAVMAN_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.TELETRAC_NAVMAN_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: false,
  },
  [EPlatformName.ZONAR]: {
    title: ETelematicsMessagesKeys.ZONAR_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.ZONAR_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.ZONAR_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.ZONAR_BANNER_TEXT,
    isNeedSnackbar: true,
  },
  [EPlatformName.MICHELIN_CONNECTED_FLEET]: {
    title: "",
    url: ETelematicsMessagesKeys.MICHELIN_CONNECTED_FLEET_INSTRUCTIONS_URL,
    fieldName:
      ETelematicsMessagesKeys.MICHELIN_CONNECTED_FLEET_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.MICHELIN_CONNECTED_FLEET_BANNER_TEXT,
    isNeedSnackbar: true,
  },
  [EPlatformName.NEXTRAQ_MARCUS]: {
    title: "",
    url: ETelematicsMessagesKeys.NEXTRAQ_MARCUS_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.NEXTRAQ_MARCUS_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.NEXTRAQ_MARCUS_BANNER_TEXT,
    isNeedSnackbar: true,
  },
  [EPlatformName.VESTIGE]: {
    title: "",
    url: ETelematicsMessagesKeys.VESTIGE_VIEW_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.VESTIGE_VIEW_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.VESTIGE_VIEW_APPROVAL_NOTE,
    isNeedSnackbar: true,
  },
  [EPlatformName.MATRACK]: {
    title: ETelematicsMessagesKeys.MATRACK_ACCOUNT_NAME_TITLE,
    url: ETelematicsMessagesKeys.MATRACK_INSTRUCTIONS_URL,
    fieldName: ETelematicsMessagesKeys.MATRACK_ACCOUNT_NAME_FIELD_TITLE,
    isNeedSnackbar: false,
  },
  [EPlatformName.GTS]: {
    title: "",
    url: "",
    fieldName: ETelematicsMessagesKeys.GTS_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.GTS_APPROVAL_NOTE,
    isNeedSnackbar: true,
  },
  [EPlatformName.VOYOMOTIVE]: {
    title: "",
    url: "",
    fieldName: ETelematicsMessagesKeys.VOYOMOTIVE_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.VOYOMOTIVE_APPROVAL_NOTE,
    isNeedSnackbar: true,
  },
  [EPlatformName.TXT_ELD]: {
    title: "",
    url: "",
    fieldName: ETelematicsMessagesKeys.TXT_ELD_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.TXT_ELD_APPROVAL_NOTE,
    isNeedSnackbar: true,
  },
};

const currentText = computed<{
  title: string;
  url: string;
  fieldName: string;
  titleParams?: Record<string, string>;
  fieldNameParams?: Record<string, string>;
  snackbarText?: string;
  isNeedSnackbar: boolean;
}>(() => {
  if (DRIVE_HOS_FAMILY.has(props.platform.const)) {
    return {
      title: "",
      url: "",
      fieldName: ETelematicsMessagesKeys.DRIVEHOS_ACCOUNT_NAME_FIELD_TITLE,
      fieldNameParams: { provider: props.platform.label },
      snackbarText: ETelematicsMessagesKeys.DRIVEHOS_APPROVAL_NOTE,
      isNeedSnackbar: true,
    };
  }

  if (TT_ELD_FAMILY.has(props.platform.const)) {
    return {
      title: ETelematicsMessagesKeys.TT_ELD_BASIC_TITLE,
      url: ETelematicsMessagesKeys.TT_ELD_INSTRUCTIONS_URL,
      fieldName: ETelematicsMessagesKeys.TT_ELD_BASIC_API_KEY_TITLE,
      isNeedSnackbar: false,
    };
  }

  if (isHosConnectPlatform(props.platform)) {
    return {
      title: ETelematicsMessagesKeys.HOS_247_ACCOUNT_NAME_TITLE,
      url: ETelematicsMessagesKeys.HOS_247_INSTRUCTIONS_URL,
      fieldName: ETelematicsMessagesKeys.HOS_247_COMPANY_ID_TITLE,
      isNeedSnackbar: false,
    };
  }

  return {
    title: "",
    url: "",
    fieldName: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_FIELD_TITLE,
    snackbarText: ETelematicsMessagesKeys.BASE_ACCOUNT_NAME_WO_CONF_CREDS_BANER,
    isNeedSnackbar: true,
    ...textByPlatformName[props.platform.const],
  };
});

__expose({
  hasError,
});

return (_ctx: any,_cache: any) => {
  const _component_d_input = _resolveComponent("d-input")!
  const _component_d_snackbar = _resolveComponent("d-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      (currentText.value.title?.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.$t(currentText.value.title, currentText.value.titleParams)), 1))
        : _createCommentVNode("", true),
      (currentText.value.url)
        ? (_openBlock(), _createBlock(HelpLink, {
            key: 1,
            link: _ctx.$t(currentText.value.url)
          }, null, 8, ["link"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_d_input, {
      ref_key: "accountNumberControl",
      ref: accountNumberControl,
      class: "drv-my-2 drv-form-control-adaptive",
      modelValue: accountNumberModel.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((accountNumberModel).value = $event)),
      disable: _ctx.disable,
      label: _ctx.$t(currentText.value.fieldName, currentText.value.fieldNameParams),
      "lazy-rules": "",
      rules: [
        _unref(requiredValidator)(_ctx.$t(_unref(ECommonMessages).REQUIRED_ERROR)),
        _unref(maxLengthValidator)(
          100,
          _ctx.$t(_unref(ECommonMessages).MAX_LENGTH_ERROR, { length: 100 })
        ),
        _unref(minLengthValidator)(
          2,
          _ctx.$t(_unref(ECommonMessages).MIN_LENGTH_ERROR, { length: 2 })
        ),
      ]
    }, null, 8, ["modelValue", "disable", "label", "rules"]),
    (currentText.value.isNeedSnackbar)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_d_snackbar, {
            icon: "triangle-exclamation",
            class: "drv-bg-color-b100 drv-color-b600",
            message: _ctx.$t(currentText.value.snackbarText || '')
          }, null, 8, ["message"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})